import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Trophy, Star, Flame, Medal, Crown, Users, Target,
  TrendingUp, Award, Zap, ChevronRight, Lock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

const AchievementsPage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [goals, setGoals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [leaderboardPeriod, setLeaderboardPeriod] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data } = await api.get(apiUrl(`/gamification/leaderboard?period=${leaderboardPeriod}`));
      setLeaderboard(data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  }, [leaderboardPeriod]);

  useEffect(() => {
    if (activeTab === 'leaderboard') {
      fetchLeaderboard();
    }
  }, [activeTab, fetchLeaderboard]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [profileRes, badgesRes, goalsRes] = await Promise.all([
        api.get(apiUrl('/gamification/profile')),
        api.get(apiUrl('/gamification/badges')),
        api.get(apiUrl('/preferences/goals')),
      ]);

      setProfile(profileRes.data);
      setBadges(badgesRes.data?.badges || []);
      setGoals(goalsRes.data);

      // Check for new badges
      await api.post(apiUrl('/gamification/badges/check'));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
      </div>
    );
  }

  return (
    <div className="f500-page space-y-6" data-testid="achievements-page">
      {/* Header with Level */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 p-6 md:p-8">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10" />
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20  bg-white/20 backdrop-blur flex items-center justify-center">
                <span className="text-4xl font-bold">{profile?.level?.level || 1}</span>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  {profile?.level?.name || 'Freshman'}
                </h1>
                <p className="text-[var(--isp-body)]">
                  {profile?.total_xp?.toLocaleString() || 0} XP Total
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm text-[var(--isp-body)]">Next Level</p>
                <p className="font-bold text-[var(--isp-heading)]">
                  {profile?.level?.max_xp ? `${(profile.level.max_xp - profile.total_xp).toLocaleString()} XP` : 'Max Level'}
                </p>
              </div>
              <Zap className="w-8 h-8 text-yellow-300" />
            </div>
          </div>
          
          {/* XP Progress Bar */}
          <div className="mt-6">
            <Progress 
              value={profile?.level?.progress_percent || 0} 
              className="h-3 bg-white/20"
            />
            <p className="text-sm text-[var(--isp-body)] mt-2">
              {profile?.level?.progress_percent?.toFixed(1) || 0}% to next level
            </p>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Trophy} label="Badges Earned" value={profile?.badge_count || 0} color="yellow" />
        <StatCard icon={Flame} label="Current Streak" value={goals?.progress?.current_streak || 0} suffix="days" color="orange" />
        <StatCard icon={Target} label="Daily Goal" value={goals?.completion?.daily_hours_pct || 0} suffix="%" color="green" />
        <StatCard icon={Star} label="Total XP" value={profile?.total_xp || 0} color="purple" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--isp-glass-border)] pb-2 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview', icon: TrendingUp },
          { id: 'badges', label: 'Badges', icon: Award },
          { id: 'goals', label: 'Goals', icon: Target },
          { id: 'leaderboard', label: 'Leaderboard', icon: Trophy }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                : 'text-muted-foreground hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid gap-6 md:grid-cols-2"
          >
            {/* Recent Badges */}
            <Card className="p-5 ">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-400" />
                Recent Badges
              </h3>
              <div className="space-y-3">
                {badges.filter(b => b.earned).slice(0, 4).map((badge) => (
                  <div key={badge.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/5">
                    <span className="text-2xl">{badge.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{badge.name}</p>
                      <p className="text-xs text-muted-foreground">{badge.description}</p>
                    </div>
                    <span className="text-xs text-purple-400">+{badge.xp_reward} XP</span>
                  </div>
                ))}
                {badges.filter(b => b.earned).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Complete activities to earn badges!
                  </p>
                )}
              </div>
            </Card>

            {/* Today's Progress */}
            <Card className="p-5 ">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-green-400" />
                Today's Progress
              </h3>
              <div className="space-y-4">
                <GoalProgress 
                  label="Study Hours" 
                  current={goals?.progress?.daily_study_hours || 0}
                  target={goals?.goals?.daily_study_hours || 2}
                  unit="hrs"
                />
                <GoalProgress 
                  label="Lectures" 
                  current={goals?.progress?.daily_lectures || 0}
                  target={goals?.goals?.daily_lectures || 1}
                  unit=""
                />
                <GoalProgress 
                  label="Weekly Flashcards" 
                  current={goals?.progress?.weekly_flashcards || 0}
                  target={goals?.goals?.weekly_flashcards || 50}
                  unit=""
                />
              </div>
            </Card>
          </motion.div>
        )}

        {activeTab === 'badges' && (
          <motion.div
            key="badges"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {['study', 'social', 'achievement', 'special'].map((category) => (
              <div key={category} className="mb-6">
                <h3 className="font-semibold mb-3 capitalize text-lg">{category} Badges</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {badges.filter(b => b.category === category).map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} />
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {activeTab === 'goals' && (
          <motion.div
            key="goals"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <GoalsSettings goals={goals} onUpdate={fetchData} />
          </motion.div>
        )}

        {activeTab === 'leaderboard' && (
          <motion.div
            key="leaderboard"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Podium - Top 3 */}
            {leaderboard.length >= 3 && (
              <div className="flex items-end justify-center gap-4 py-6" data-testid="leaderboard-podium">
                {/* 2nd Place */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-gray-300/30 to-gray-500/30 border-2 border-gray-400/50 flex items-center justify-center text-2xl font-bold text-gray-300 mb-2">
                    {leaderboard[1]?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <p className="text-sm font-medium text-[var(--isp-heading)] truncate max-w-[100px]">{leaderboard[1]?.name}</p>
                  <p className="text-xs text-gray-400">{leaderboard[1]?.total_xp?.toLocaleString()} XP</p>
                  <div className="mt-2 w-20 h-20 mx-auto rounded-t-xl bg-gradient-to-b from-gray-500/30 to-gray-600/30 flex items-end justify-center pb-2">
                    <span className="text-2xl font-bold text-gray-300">2</span>
                  </div>
                </motion.div>

                {/* 1st Place */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-center -mt-4"
                >
                  <div className="relative">
                    <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-yellow-400/30 to-amber-500/30 border-2 border-yellow-400/60 flex items-center justify-center text-3xl font-bold text-yellow-300 mb-2">
                      {leaderboard[0]?.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  </div>
                  <p className="text-sm font-bold text-[var(--isp-heading)] truncate max-w-[120px]">{leaderboard[0]?.name}</p>
                  <p className="text-xs text-yellow-400 font-medium">{leaderboard[0]?.total_xp?.toLocaleString()} XP</p>
                  <div className="mt-2 w-24 h-28 mx-auto rounded-t-xl bg-gradient-to-b from-yellow-500/30 to-amber-600/30 flex items-end justify-center pb-2">
                    <span className="text-3xl font-bold text-yellow-300">1</span>
                  </div>
                </motion.div>

                {/* 3rd Place */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-orange-400/30 to-orange-600/30 border-2 border-orange-400/50 flex items-center justify-center text-2xl font-bold text-orange-300 mb-2">
                    {leaderboard[2]?.name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <p className="text-sm font-medium text-[var(--isp-heading)] truncate max-w-[100px]">{leaderboard[2]?.name}</p>
                  <p className="text-xs text-orange-400">{leaderboard[2]?.total_xp?.toLocaleString()} XP</p>
                  <div className="mt-2 w-20 h-16 mx-auto rounded-t-xl bg-gradient-to-b from-orange-500/30 to-orange-600/30 flex items-end justify-center pb-2">
                    <span className="text-2xl font-bold text-orange-300">3</span>
                  </div>
                </motion.div>
              </div>
            )}

            <Card className="p-5 ">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  XP Leaderboard
                </h3>
                <div className="flex gap-2">
                  {['all', 'monthly', 'weekly'].map((period) => (
                    <button
                      key={period}
                      onClick={() => setLeaderboardPeriod(period)}
                      className={`px-3 py-1 text-xs rounded-lg capitalize ${
                        leaderboardPeriod === period
                          ? 'bg-purple-500/20 text-purple-400'
                          : 'text-muted-foreground hover:text-[var(--isp-heading)]'
                      }`}
                      data-testid={`lb-period-${period}`}
                    >
                      {period === 'all' ? 'All Time' : period}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                {leaderboard.map((entry, idx) => (
                  <motion.div
                    key={entry.user_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      entry.is_current_user 
                        ? 'bg-purple-500/20 border border-purple-500/30' 
                        : 'bg-white/5 hover:bg-[var(--isp-glass-inner)]'
                    }`}
                    data-testid={`leaderboard-entry-${idx}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      idx === 1 ? 'bg-gray-400/20 text-gray-300' :
                      idx === 2 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-white/10 text-[var(--isp-body)]'
                    }`}>
                      {idx < 3 ? (
                        <Medal className={`w-5 h-5 ${idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-300' : 'text-orange-400'}`} />
                      ) : (
                        <span className="text-sm">#{entry.rank}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {entry.name}
                        {entry.is_current_user && <span className="text-xs text-purple-400 ml-2">(You)</span>}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span>Lv.{entry.level} {entry.level_name}</span>
                        <span className="flex items-center gap-0.5">
                          <Award className="w-3 h-3" /> {entry.badge_count}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-purple-400">{entry.total_xp.toLocaleString()}</p>
                      <p className="text-[10px] text-muted-foreground">XP</p>
                    </div>
                  </motion.div>
                ))}

                {leaderboard.length === 0 && (
                  <div className="text-center py-8">
                    <Trophy className="w-12 h-12 mx-auto text-white/20 mb-3" />
                    <p className="text-muted-foreground">No leaderboard data yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Complete activities to earn XP and appear here!</p>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, suffix = '', color }) => {
  const colors = {
    yellow: 'from-yellow-500/20 to-yellow-600/20 text-yellow-400',
    orange: 'from-orange-500/20 to-orange-600/20 text-orange-400',
    green: 'from-green-500/20 to-green-600/20 text-green-400',
    purple: 'from-purple-500/20 to-purple-600/20 text-purple-400',
  };

  return (
    <Card className={`p-4  bg-gradient-to-br ${colors[color]}`}>
      <Icon className="w-6 h-6 mb-2" />
      <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}{suffix}</p>
      <p className="text-xs opacity-70">{label}</p>
    </Card>
  );
};

// Badge Card Component
const BadgeCard = ({ badge }) => {
  const tierColors = {
    bronze: 'border-orange-700/50 bg-orange-900/20',
    silver: 'border-gray-400/50 bg-gray-700/20',
    gold: 'border-yellow-500/50 bg-yellow-900/20',
    platinum: 'border-cyan-400/50 bg-cyan-900/20',
  };

  return (
    <div className={`relative p-4 rounded-xl border-2 transition-all ${
      badge.earned 
        ? tierColors[badge.tier] 
        : 'border-[var(--isp-glass-border)] bg-white/5 opacity-50'
    }`}>
      {!badge.earned && (
        <Lock className="absolute top-2 right-2 w-4 h-4 text-[var(--isp-muted)]" />
      )}
      <span className="text-3xl block mb-2">{badge.icon}</span>
      <p className="font-medium text-sm">{badge.name}</p>
      <p className="text-xs text-muted-foreground line-clamp-2">{badge.description}</p>
      <p className="text-xs text-purple-400 mt-1">+{badge.xp_reward} XP</p>
    </div>
  );
};

// Goal Progress Component
const GoalProgress = ({ label, current, target, unit }) => {
  const percent = Math.min(100, (current / target) * 100);
  
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="text-muted-foreground">
          {current}{unit} / {target}{unit}
        </span>
      </div>
      <Progress value={percent} className="h-2" />
    </div>
  );
};

// Goals Settings Component — auth handled by authFetch via cookies; no
// token prop needed (legacy leftover removed in iter197).
const GoalsSettings = ({ goals, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    daily_study_hours: goals?.goals?.daily_study_hours || 2,
    weekly_study_hours: goals?.goals?.weekly_study_hours || 10,
    daily_lectures: goals?.goals?.daily_lectures || 1,
    weekly_flashcards: goals?.goals?.weekly_flashcards || 50,
    streak_target: goals?.goals?.streak_target || 7,
  });

  const handleSave = async () => {
    try {
      const res = await authFetch(`${API}/api/preferences/goals`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Goals updated!');
        setEditing(false);
        onUpdate();
      }
    } catch (error) {
      toast.error('Failed to update goals');
    }
  };

  return (
    <Card className="p-5 ">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Study Goals</h3>
        <Button
          size="sm"
          variant={editing ? "default" : "outline"}
          onClick={() => editing ? handleSave() : setEditing(true)}
          className="rounded-lg"
        >
          {editing ? 'Save' : 'Edit Goals'}
        </Button>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2">
        <GoalInput
          label="Daily Study Hours"
          value={formData.daily_study_hours}
          onChange={(v) => setFormData({...formData, daily_study_hours: v})}
          disabled={!editing}
          min={0.5}
          max={12}
          step={0.5}
        />
        <GoalInput
          label="Weekly Study Hours"
          value={formData.weekly_study_hours}
          onChange={(v) => setFormData({...formData, weekly_study_hours: v})}
          disabled={!editing}
          min={1}
          max={50}
          step={1}
        />
        <GoalInput
          label="Daily Lectures"
          value={formData.daily_lectures}
          onChange={(v) => setFormData({...formData, daily_lectures: v})}
          disabled={!editing}
          min={1}
          max={10}
          step={1}
        />
        <GoalInput
          label="Weekly Flashcards"
          value={formData.weekly_flashcards}
          onChange={(v) => setFormData({...formData, weekly_flashcards: v})}
          disabled={!editing}
          min={10}
          max={500}
          step={10}
        />
        <GoalInput
          label="Streak Target (days)"
          value={formData.streak_target}
          onChange={(v) => setFormData({...formData, streak_target: v})}
          disabled={!editing}
          min={3}
          max={365}
          step={1}
        />
      </div>
    </Card>
  );
};

// Goal Input Component
const GoalInput = ({ label, value, onChange, disabled, min, max, step }) => (
  <div>
    <label className="text-sm text-muted-foreground block mb-1">{label}</label>
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--isp-glass-border)] disabled:opacity-50"
    />
  </div>
);

export default AchievementsPage;
